import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ToggleSwitch from './ToggleSwitch';
import { updateAutoSyncPref } from '../redux/slices/djAutoSyncSlice';
import { track } from '../utils/analytics';
import './AutoSyncToggle.scss';

/**
 * The per-app Auto-sync switch shown in each DJ sidebar's synced view.
 * Enabling makes AutoSyncManager import watcher-detected changes without a
 * click; the "include new" sub-switch additionally lets never-imported
 * playlists arrive without the review picker. Uses the app's ToggleSwitch
 * (same control as the Prepare-tab settings).
 */
export default function AutoSyncToggle({ app, label, noun = 'playlist', from = 'sidebar' }) {
    const dispatch = useDispatch();
    const prefs = useSelector((s) => s.djAutoSync.prefs[app]) || { enabled: false, includeNew: false };

    const toggleEnabled = () => {
        const enabled = !prefs.enabled;
        dispatch(updateAutoSyncPref({ app, enabled }));
        if (enabled) track('dj_auto_sync_enabled', { app, from, include_new: prefs.includeNew });
        else track('dj_auto_sync_disabled', { app, from });
    };

    const toggleIncludeNew = () => {
        const includeNew = !prefs.includeNew;
        dispatch(updateAutoSyncPref({ app, includeNew }));
        track('dj_auto_sync_include_new_toggled', { app, from, include_new: includeNew });
    };

    return (
        <div className="auto-sync-toggle">
            <div className="auto-sync-row">
                <ToggleSwitch
                    id={`auto-sync-${from}-${app}`}
                    checked={prefs.enabled}
                    onChange={toggleEnabled}
                    label={(
                        <span className="auto-sync-labels">
                            <span className="auto-sync-title">Auto-sync</span>
                            <span className="auto-sync-hint">
                                {prefs.enabled
                                    ? `New tracks and ${noun} changes from ${label} import themselves`
                                    : `Import changes from ${label} automatically`}
                            </span>
                        </span>
                    )}
                />
            </div>
            {prefs.enabled && (
                <div className="auto-sync-row auto-sync-subrow">
                    <ToggleSwitch
                        id={`auto-sync-new-${from}-${app}`}
                        checked={prefs.includeNew}
                        onChange={toggleIncludeNew}
                        label={(
                            <span className="auto-sync-labels">
                                <span className="auto-sync-title">Include new {noun}s</span>
                                <span className="auto-sync-hint">
                                    {prefs.includeNew
                                        ? `Brand-new ${noun}s import without review`
                                        : `Brand-new ${noun}s still wait for your review`}
                                </span>
                            </span>
                        )}
                    />
                </div>
            )}
        </div>
    );
}
